import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Monitor } from 'lucide-react';
import type { CajaEmparejadaVista } from '@shared/types';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 156 — Qué computadoras reciben los tickets de este negocio, y cómo quitar una.
 *
 * ## El defecto que cierra
 *
 * El negocio tenía dos cajas emparejadas, las dos llamadas "Caja". Una era la computadora del
 * restaurante; la otra, la laptop de pruebas del desarrollador, en otra casa. Cada ticket se lo
 * llevaba la primera que preguntara: comandas que no salían en cocina (Sprint 149), tickets que el
 * sistema daba por impresos y nunca salieron (26/09). Y no había manera de verlo ni de quitarla:
 * se descubrió leyendo la base a mano.
 *
 * Por eso cada caja se muestra con **el nombre de la computadora** —lo único que distingue dos
 * "Caja"— y con un botón para desconectarla. Y si dos están vivas a la vez, se dice arriba y en
 * palabras de mostrador: un ticket repartido al azar no es un detalle técnico, es papel que falta.
 *
 * Sólo el dueño: los dos canales no están declarados para ningún otro oficio.
 */
export function CajasConectadas({ repartidas }: { repartidas: boolean }) {
  const [cajas, setCajas] = useState<CajaEmparejadaVista[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quitando, setQuitando] = useState<number | null>(null);

  const cargar = useCallback(async () => {
    const r = await window.api.impresion.cajas();
    if (r.ok) setCajas(r.data);
  }, []);

  useEffect(() => {
    void cargar();
    const id = window.setInterval(() => void cargar(), 10_000);
    return () => window.clearInterval(id);
  }, [cargar]);

  /** Sprint 158 (DT-161) — "Sí, es de mi negocio": la caja deja de presentarse como nueva. */
  const reconocer = async (c: CajaEmparejadaVista) => {
    setError(null);
    const r = await window.api.impresion.reconocerCaja(c.id);
    if (!r.ok) setError(traducirErrorBackend(r.error, r.codigo));
    await cargar();
  };

  const desconectar = async (c: CajaEmparejadaVista) => {
    const donde = c.equipo ? `la computadora ${c.equipo}` : 'esa computadora';
    const ok = await window.confirmar({
      titulo: `¿Desconectar ${donde}?`,
      mensaje:
        'Deja de recibir tickets en ese mismo momento. Si es la computadora del restaurante, los ' +
        'tickets vuelven a salir con la ventana de imprimir hasta que la conectes otra vez con un ' +
        'código nuevo.',
      textoConfirmar: 'Sí, desconectarla',
      textoCancelar: 'Cancelar',
      variant: 'warn',
    });
    if (!ok) return;
    setQuitando(c.id);
    setError(null);
    const r = await window.api.impresion.desconectarCaja(c.id);
    setQuitando(null);
    if (!r.ok) setError(traducirErrorBackend(r.error, r.codigo));
    await cargar();
  };

  if (!cajas || cajas.length === 0) return null;

  const hora = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleString('es-MX', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'nunca';

  return (
    <div className="mt-3 space-y-2">
      {repartidas && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-lg border-2 border-amber-400 bg-amber-50 p-3"
        >
          <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-amber-700" aria-hidden />
          <div className="text-sm text-amber-950">
            <p className="text-base font-bold">Dos computadoras están recibiendo tus tickets</p>
            <p className="mt-1">
              Cada ticket sale sólo en una de las dos, la que pregunte primero. Si una de ellas no
              está en el restaurante, esos tickets se imprimen donde nadie los ve.{' '}
              <b>Desconecta la que no reconozcas.</b>
            </p>
          </div>
        </div>
      )}

      <p className="text-sm font-semibold text-slate-800">Computadoras conectadas</p>
      <ul className="space-y-2">
        {cajas.map((c) => (
          <li
            key={c.id}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-300 bg-white p-3"
          >
            <Monitor className="h-6 w-6 shrink-0 text-slate-600" aria-hidden />
            <div className="min-w-0 flex-1 text-sm text-slate-800">
              <p className="text-base font-semibold text-slate-900">
                {c.equipo ?? 'Computadora sin identificar'}
                {!c.reconocida && (
                  <span className="ml-2 rounded bg-amber-200 px-2 py-0.5 text-sm font-bold text-amber-950">
                    Nueva
                  </span>
                )}
              </p>
              <p>
                {c.conectado ? (
                  <span className="font-medium text-green-800">Responde ahora</span>
                ) : (
                  <span className="font-medium text-amber-800">Última señal: {hora(c.ultimoContacto)}</span>
                )}
                {' · '}conectada el {hora(c.emparejada)}
              </p>
              <p className="text-slate-700">
                Imprime en: {c.impresoraCaja ?? 'sin impresora elegida'}
                {c.impresoraCocina ? ` · cocina: ${c.impresoraCocina}` : ''}
              </p>
              {!c.equipo && (
                <p className="text-slate-700">
                  El nombre aparece en cuanto el programa de esa caja se actualice.
                </p>
              )}
            </div>
            {!c.reconocida && (
              <button
                type="button"
                onClick={() => void reconocer(c)}
                className="min-h-[44px] rounded-lg bg-green-700 px-4 py-2 text-sm font-semibold text-white hover:bg-green-800"
              >
                Sí, es de mi negocio
              </button>
            )}
            <button
              type="button"
              onClick={() => void desconectar(c)}
              disabled={quitando === c.id}
              className="min-h-[44px] rounded-lg border-2 border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-800 hover:bg-red-50 disabled:opacity-60"
            >
              {quitando === c.id ? 'Desconectando…' : 'Desconectar'}
            </button>
          </li>
        ))}
      </ul>
      {error && <p className="text-sm text-red-700">{error}</p>}
    </div>
  );
}
